import { useEffect, useState } from 'react'

import { BUSINESS_TIMEZONE, shortDate } from '../../lib/datetime'
import type { ShareLink } from '../../lib/workspace'

/**
 * Share a preview (01 §3.5 Sharing, D27).
 *
 * A homeowner who has just seen their bathroom with a new vanity in it wants to show somebody,
 * and that is the cheapest marketing this business will ever get. It is also a photograph of
 * the inside of their house, so the sheet says the two things that matter — **seven days** and
 * **watermarked** — where they cannot be missed, and Web Share is used where the platform has
 * it so the link goes wherever they already talk to people.
 */

interface Props {
  open: boolean
  link: ShareLink | null
  busy: boolean
  onClose: () => void
}

export function ShareSheet({ open, link, busy, onClose }: Props) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2200)
    return () => clearTimeout(timer)
  }, [copied])

  if (!open) return null

  async function share() {
    if (!link) return
    const payload = {
      title: 'My renovation — ASB Home Renovations',
      text: 'Here is what my room could look like.',
      url: link.url,
    }
    if (navigator.share) {
      try {
        await navigator.share(payload)
        return
      } catch {
        // The homeowner dismissed the share sheet. Falling through to copy is the right
        // recovery: they still have the link.
      }
    }
    await navigator.clipboard.writeText(link.url)
    setCopied(true)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 sm:items-center sm:p-6">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0" />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-heading"
        className="relative w-full rounded-t-[20px] bg-paper p-5 shadow-lift sm:max-w-md sm:rounded-card"
      >
        <h2 id="share-heading" className="text-xl">
          Share this preview
        </h2>

        {busy && <p className="mt-3 text-muted">Making a link…</p>}

        {link && (
          <>
            {link.image_url && (
              <img
                src={link.image_url}
                alt="The preview as it will be shared"
                className="mt-4 w-full rounded-card"
              />
            )}
            <p className="mt-3 text-sm text-body">
              The link works for seven days and shows a watermarked copy. Anyone with it can see
              this one preview — nothing else about your project.
            </p>
            <p className="m-0 mt-2 break-all font-mono text-xs text-muted">{link.url}</p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void share()}
                className="min-h-12 rounded-btn bg-blue px-5 font-semibold text-white shadow-blue"
              >
                {copied ? 'Copied' : 'Share'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="min-h-12 rounded-btn border border-line bg-white px-5 font-semibold text-ink"
              >
                Done
              </button>
              <span className="ml-auto self-center text-xs text-muted">
                Expires {shortDate(link.expires_at, BUSINESS_TIMEZONE)}
              </span>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
